import { create } from 'zustand';
import { fetchBranding, type TenantBranding } from '../api/branding';
import { fetchTenantConfig } from '../api/tenant';

interface SubscriptionFeatures {
  niveau_1: boolean;
  niveau_2: boolean;
  export_csv: boolean;
  export_xlsx: boolean;
  export_pdf: boolean;
  export_geojson: boolean;
  export_shapefile: boolean;
  import_data: boolean;
  subsidies_tab: boolean;
  amortization_tab: boolean;
  planning_tab: boolean;
  population_tab: boolean;
  finance_tab: boolean;
  municipal_finance_tab: boolean;
  commune_reference: boolean;
  multi_language: boolean;
  custom_branding: boolean;
  api_access: boolean;
  // CIVITAS module flags
  module_spatial: boolean;
  module_finance: boolean;
  module_admin: boolean;
}

interface TenantState {
  branding: TenantBranding | null;
  features: SubscriptionFeatures;
  tier: string;
  loading: boolean;

  loadBranding: () => Promise<void>;
  loadFeatures: () => Promise<void>;
  setBranding: (branding: TenantBranding) => void;
  setFeatures: (features: Partial<SubscriptionFeatures>) => void;
}

const DEFAULT_FEATURES: SubscriptionFeatures = {
  niveau_1: true,
  niveau_2: true,
  export_csv: true,
  export_xlsx: true,
  export_pdf: true,
  export_geojson: true,
  export_shapefile: true,
  import_data: true,
  subsidies_tab: true,
  amortization_tab: true,
  planning_tab: true,
  population_tab: true,
  finance_tab: true,
  municipal_finance_tab: true,
  commune_reference: true,
  multi_language: true,
  custom_branding: true,
  api_access: false,
  // CIVITAS modules — disabled by default, enabled via feature flags
  module_spatial: false,
  module_finance: false,
  module_admin: false,
};

export const useTenantStore = create<TenantState>((set) => ({
  branding: null,
  features: DEFAULT_FEATURES,
  tier: 'professional',
  loading: false,

  loadBranding: async () => {
    set({ loading: true });
    try {
      const branding = await fetchBranding();
      set({ branding, loading: false });
    } catch {
      set({ loading: false });
    }
  },

  loadFeatures: async () => {
    try {
      const config = await fetchTenantConfig();
      set((s) => ({
        tier: config.tier,
        features: { ...s.features, ...config.features },
      }));
    } catch (err) {
      console.error('[tenant] request failed:', err);
    }
  },

  setBranding: (branding) => set({ branding }),

  setFeatures: (partial) =>
    set((s) => ({ features: { ...s.features, ...partial } })),
}));
